'use client'

import React from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { Search, Calendar, BarChart3, Users, UsersRound, Shield } from 'lucide-react'
import { cn, buildRegionUrl } from '@/shared/lib/utils'
import { useRegion } from '@/shared/hooks/use-region'
import {
  SEARCH_PARAMS_TAB_KEY,
  VOLUNTEER_TAB_FILTER_SEARCH,
  VOLUNTEER_DEFAULT_TAB_WHEN_STM,
  STM_TAB_LINKS,
  AR_TEAM_TAB_LINK,
  type VolunteerTabValue,
} from '../constants/volunteer-tab-links'

interface VolunteerTabsProps {
  tab?: VolunteerTabValue
  /** When true, Volunteers tab plus STM tabs (Calendar, Dashboard, Groups) are shown */
  showStmTabs: boolean
  /** When true, AR Team tab is shown (Admin/Base Leader Dates Served calendar) – enabled for all regions */
  showArTeamTab: boolean
}

const tabIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  'filter-search': Search,
  calendar: Calendar,
  dashboard: BarChart3,
  groups: Users,
  'ar-team': UsersRound,
}

function VolunteerTabs({ tab, showStmTabs, showArTeamTab }: VolunteerTabsProps) {
  const { regionSlug } = useRegion()
  const searchParams = useSearchParams()
  const defaultTab: VolunteerTabValue = showStmTabs
    ? VOLUNTEER_DEFAULT_TAB_WHEN_STM
    : showArTeamTab
      ? AR_TEAM_TAB_LINK.value
      : VOLUNTEER_TAB_FILTER_SEARCH

  // Preserve all params except tab when building tab URLs (e.g. debug=datepicker for iOS debugging)
  const buildTabParams = (tabValue: string) => {
    const params: Record<string, string> = { [SEARCH_PARAMS_TAB_KEY]: tabValue }
    searchParams.forEach((value, key) => {
      if (key !== SEARCH_PARAMS_TAB_KEY) {
        params[key] = value
      }
    })
    return params
  }

  // Hide tabs entirely if there's only Volunteers tab (no STM or AR Team tabs)
  if (!showStmTabs && !showArTeamTab) {
    return null
  }

  return (
    <div className="flex items-center justify-center gap-6 sm:gap-5 flex-wrap">
      {/* Calendar, Groups, Dashboard (STM tabs) first */}
      {showStmTabs &&
        STM_TAB_LINKS.map((link) => {
          const params = buildTabParams(link.value)
          const href = regionSlug
            ? buildRegionUrl(regionSlug, '/volunteers', params)
            : `/volunteers?${new URLSearchParams(params as Record<string, string>).toString()}`
          const Icon = tabIcons[link.value]
          return (
            <Link
              key={link.value}
              href={href}
              className={cn(
                'flex items-center justify-center gap-1.5 sm:w-[100px] pb-0.5 border-b-[3px] border-transparent transition-colors px-2 sm:px-0 py-1 sm:py-0',
                (tab === link.value || (!tab && defaultTab === link.value)) &&
                  'border-primary text-primary font-medium'
              )}
              title={link.label}
            >
              {Icon && <Icon className="h-5 w-5 sm:hidden" />}
              <span className="hidden sm:inline text-center">{link.label}</span>
            </Link>
          )
        })}

      {showArTeamTab && (
        <Link
          href={
            regionSlug
              ? buildRegionUrl(regionSlug, '/volunteers', buildTabParams(AR_TEAM_TAB_LINK.value))
              : `/volunteers?${new URLSearchParams(buildTabParams(AR_TEAM_TAB_LINK.value) as Record<string, string>).toString()}`
          }
          className={cn(
            'flex items-center justify-center gap-1.5 sm:w-[100px] pb-0.5 border-b-[3px] border-transparent transition-colors px-2 sm:px-0 py-1 sm:py-0',
            (tab === AR_TEAM_TAB_LINK.value || (!tab && defaultTab === AR_TEAM_TAB_LINK.value)) &&
              'border-primary text-primary font-medium'
          )}
          title={AR_TEAM_TAB_LINK.label}
        >
          <Shield className="h-5 w-5 sm:hidden" />
          <span className="hidden sm:inline text-center">{AR_TEAM_TAB_LINK.label}</span>
        </Link>
      )}

      {/* Volunteers tab last */}
      <Link
        href={
          regionSlug
            ? buildRegionUrl(regionSlug, '/volunteers', buildTabParams(VOLUNTEER_TAB_FILTER_SEARCH))
            : `/volunteers?${new URLSearchParams(buildTabParams(VOLUNTEER_TAB_FILTER_SEARCH) as Record<string, string>).toString()}`
        }
        className={cn(
          'flex items-center justify-center gap-1.5 sm:w-[100px] pb-0.5 border-b-[3px] border-transparent transition-colors px-2 sm:px-0 py-1 sm:py-0',
          (tab === VOLUNTEER_TAB_FILTER_SEARCH || (!tab && defaultTab === VOLUNTEER_TAB_FILTER_SEARCH)) &&
            'border-primary text-primary font-medium'
        )}
        title="Volunteers"
      >
        <Search className="h-5 w-5 sm:hidden" />
        <span className="hidden sm:inline text-center">Volunteers</span>
      </Link>
    </div>
  )
}

export default VolunteerTabs
